import { type MouseEvent, useCallback, useMemo, useState } from 'react';

import type { UiEntitySpec } from '../config/uiSpec';
import { useEntityList } from '../data/useEntityList';
import type { UseEntityListParams } from '../data/useEntityList';
import { useEntityDelete } from '../data/useEntityDelete';
import { Card } from '../ui/Card';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { DataTable } from '../ui/DataTable';
import type { DataTableColumn } from '../ui/DataTable';

export interface EntityListScreenProps<TFilter = unknown> {
  key?: React.Key;
  entity: UiEntitySpec;
  filters?: TFilter;
  pageSize?: number;
  titleOverride?: string;
  onRowClick?: (row: Record<string, unknown>) => void;
}

export function EntityListScreen<TFilter = unknown>(
  props: EntityListScreenProps<TFilter>
): JSX.Element {
  const [page, setPage] = useState(0);
  const pageSize = props.pageSize ?? 20;
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const { entity, filters } = props;

  const params: UseEntityListParams<TFilter> = useMemo(
    () => ({
      entity: entity.entityName,
      filters,
      page,
      pageSize,
      sort: entity.list?.defaultSort
        ? {
            field: entity.list.defaultSort.field,
            direction: entity.list.defaultSort.direction,
          }
        : undefined,
    }),
    [entity.entityName, entity.list?.defaultSort, filters, page, pageSize]
  );

  const { data, total, isLoading, isError, error, refetch } = useEntityList<
    Record<string, unknown>,
    TFilter
  >(params);

  const deleteMutation = useEntityDelete({
    entity: entity.entityName,
    onSuccess: () => {
      refetch();
    },
  });

  const getRowId = (row: Record<string, unknown>): string | number | undefined => {
    const id = row.id;
    return typeof id === 'string' || typeof id === 'number' ? id : undefined;
  };

  const handleDeleteRow = useCallback(
    async (row: Record<string, unknown>) => {
      const rowId = getRowId(row);
      if (!rowId) return;

      const confirmed = window.confirm(`Are you sure you want to delete this ${entity.label}?`);
      if (!confirmed) return;

      setDeletingId(rowId);

      try {
        await deleteMutation.mutate(rowId);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteMutation, entity.label]
  );

  const columns: DataTableColumn<Record<string, unknown>>[] = useMemo(
    () => {
      const baseColumns = (entity.list?.columns ?? []).map((column) => ({
        id: column.id,
        header: column.label,
        width: column.width,
        align: column.type === 'number' ? ('right' as const) : ('left' as const),
        accessor: (row: Record<string, unknown>) => {
          const value = row[column.field];

          if (value == null) {
            return '';
          }

          if (column.type === 'date') {
            const dateInput =
              typeof value === 'string' || typeof value === 'number' || value instanceof Date
                ? new Date(value)
                : null;

            if (dateInput && !Number.isNaN(dateInput.getTime())) {
              return dateInput.toLocaleDateString();
            }

            return String(value);
          }

          return String(value);
        },
      }));

      return [
        ...baseColumns,
        {
          id: 'actions',
          header: '',
          align: 'right' as const,
          accessor: (row: Record<string, unknown>) => (
            <button
              type="button"
              className="button button-secondary button-danger"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                handleDeleteRow(row);
              }}
              disabled={
                deletingId === getRowId(row) || deleteMutation.isLoading || deleteMutation.isError
              }
            >
              {deletingId === getRowId(row) && deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
            </button>
          ),
        } as DataTableColumn<Record<string, unknown>>,
      ];
    },
    [deleteMutation.isError, deleteMutation.isLoading, deletingId, entity.list?.columns, handleDeleteRow]
  );

  const errorNode = isError ? (
    <span className="text-sm text-red-600">
      {error instanceof Error ? error.message : 'Failed to load data'}
    </span>
  ) : undefined;

  const title = props.titleOverride ?? entity.label;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={title} />

      <Card>
        <DataTable<Record<string, unknown>>
          columns={columns}
          data={data ?? []}
          isLoading={isLoading}
          error={errorNode}
          page={page}
          pageSize={pageSize}
          totalCount={total}
          onPageChange={setPage}
          onRowClick={props.onRowClick}
          emptyState={
            <EmptyState
              title="No records"
              description="There are no records to display for this entity yet."
            />
          }
        />
      </Card>
    </div>
  );
}
